import { useEffect, useMemo, useRef, useState } from 'react';
import { Chess } from 'chess.js';
import {
  MISTAKE_LABELS,
  MISTAKE_REASONS,
  REASON_INFO,
  checkedKing,
  explainSan,
  formatEval,
  startPuzzle,
  winChance,
  type MistakeReason,
} from '@familyhub/shared';
import { Board, type Arrow } from './Board';
import { gameLink, mistakePuzzle } from './common';
import type { GameRecord, MistakeRecord } from './db';

/**
 * A mistake from his own game, reviewed (docs/SCHAAK.md §3.2.2, §3.7). The
 * board goes back to the moment before his move. His move is a red arrow, the
 * better ones numbered green arrows. Two eval bars show what his move did to
 * Stockfish's score.
 *
 * Seeing is not understanding, so two lines can be **played out** on the board:
 * *Waarom fout?* (the engine's best reply to his move and what follows) and,
 * by tapping a better move, that move's own line. Then he is asked once, in his
 * own words, what went wrong. Naming it is what the "why I make mistakes" count
 * and the focus advice are built from.
 */

type Better = NonNullable<MistakeRecord['better']>[number];

/** The better moves to show: the stored ones, or (rows from before 045) just the best. */
export function betterMoves(m: MistakeRecord): Better[] {
  if (m.better?.length) return m.better;
  return [{ move: m.best, san: m.best_san, cp: null, mate: null }];
}

const hasEval = (cp: number | null | undefined, mate: number | null | undefined) => cp != null || mate != null;

/** A horizontal eval bar: his share of the winning chances, Lichess's curve. */
function EvalBar({ cp, mate, label }: { cp: number | null; mate: number | null; label: string }) {
  const pct = Math.round(50 + 50 * winChance({ cp, mate }));
  return (
    <div className="sk-evalrow">
      <span className="sk-evallabel">{label}</span>
      <span className="sk-evalbar" role="img" aria-label={`${label}: ${formatEval({ cp, mate })}`}>
        <span className="sk-evalfill" style={{ width: `${pct}%` }} />
        <span className="sk-evalmid" />
      </span>
      <span className="sk-evalnum">{formatEval({ cp, mate })}</span>
    </div>
  );
}

/** A line being played out: where it starts, its moves (UCI), how far along. */
interface Line {
  title: string;
  baseFen: string;
  moves: string[];
  /** Moves already on the board. */
  at: number;
  /** The move that led to baseFen, shown as the last move before the line starts. */
  before: [string, string] | null;
  tone: 'good' | 'bad';
}

function playLine(line: Line): { fen: string; sans: string[]; last: [string, string] | null } {
  const chess = new Chess(line.baseFen);
  const sans: string[] = [];
  let last = line.before;
  for (const [i, m] of line.moves.entries()) {
    let mv;
    try {
      mv = chess.move({ from: m.slice(0, 2), to: m.slice(2, 4), promotion: m[4] });
    } catch {
      break;
    }
    sans.push(mv.san);
    if (i < line.at) last = [mv.from, mv.to];
  }
  // Replay only up to `at` for the position shown.
  const shown = new Chess(line.baseFen);
  for (const m of line.moves.slice(0, line.at)) {
    try {
      shown.move({ from: m.slice(0, 2), to: m.slice(2, 4), promotion: m[4] });
    } catch {
      break;
    }
  }
  return { fen: shown.fen(), sans, last };
}

function NavIcon({ d }: { d: string }) {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d={d} />
    </svg>
  );
}

export function MistakeReview({
  mistake: m,
  game,
  orientation,
  reason,
  onReason,
}: {
  mistake: MistakeRecord;
  game: GameRecord | undefined;
  orientation: 'w' | 'b';
  reason: MistakeReason | null;
  onReason: (r: MistakeReason) => void;
}) {
  const start = useMemo(() => startPuzzle(mistakePuzzle(m)), [m]);
  const better = betterMoves(m);
  const [focus, setFocus] = useState<number | null>(null);
  const [line, setLine] = useState<Line | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => void (timer.current && clearTimeout(timer.current)), []);

  const scored = hasEval(m.eval_before_cp, m.eval_before_mate) && hasEval(m.eval_after_cp, m.eval_after_mate);
  const afterHis = useMemo(() => {
    const c = new Chess(start.fen);
    try {
      c.move({ from: m.played.slice(0, 2), to: m.played.slice(2, 4), promotion: m.played[4] });
    } catch {
      return null;
    }
    return c.fen();
  }, [start.fen, m.played]);

  // Step a line forward on its own, a move at a time, so it reads like a replay.
  const open = (l: Line) => {
    if (timer.current) clearTimeout(timer.current);
    setLine(l);
    const step = (at: number) => {
      if (at > l.moves.length) return;
      setLine((cur) => (cur && cur.title === l.title ? { ...cur, at } : cur));
      timer.current = setTimeout(() => step(at + 1), 900);
    };
    timer.current = setTimeout(() => step(1), 500);
  };
  const stopAuto = () => {
    if (timer.current) clearTimeout(timer.current);
  };

  const refutation = m.refutation ?? [];
  const openRefutation = () =>
    afterHis &&
    open({
      title: `Waarom ${m.played_san} fout is`,
      baseFen: afterHis,
      moves: refutation,
      at: 0,
      before: [m.played.slice(0, 2), m.played.slice(2, 4)],
      tone: 'bad',
    });
  const openBetter = (i: number) => {
    const b = better[i]!;
    setFocus(i);
    open({
      title: `${b.san}: wat het oplevert`,
      baseFen: start.fen,
      moves: b.pv?.length ? b.pv : [b.move],
      at: 0,
      before: start.lastMove,
      tone: 'good',
    });
  };

  const arrows: Arrow[] = line
    ? []
    : [
        ...better.map((b, i) => ({
          from: b.move.slice(0, 2),
          to: b.move.slice(2, 4),
          tone: 'good' as const,
          label: String(i + 1),
          faded: focus != null && focus !== i,
        })),
        // His own move last, so it is drawn on top.
        { from: m.played.slice(0, 2), to: m.played.slice(2, 4), tone: 'bad' as const, faded: focus != null && focus !== -1 },
      ];

  const played = line ? playLine(line) : null;
  const fen = played?.fen ?? start.fen;

  return (
    <>
      <Board
        fen={fen}
        orientation={orientation}
        selected={null}
        targets={[]}
        lastMove={played ? played.last : start.lastMove}
        check={checkedKing(fen)}
        arrows={arrows}
        disabled
        onSquare={() => {}}
      />

      {line && played ? (
        <section className={`sk-line is-${line.tone}`} aria-label={line.title}>
          <div className="sk-line-head">
            <h3>{line.title}</h3>
            <button className="btn btn-quiet" onClick={() => (stopAuto(), setLine(null), setFocus(null))}>
              Sluiten
            </button>
          </div>
          <ol className="sk-line-moves">
            {played.sans.map((san, i) => (
              <li key={i}>
                <button
                  className={i < line.at ? 'is-played' : ''}
                  aria-current={i === line.at - 1 || undefined}
                  onClick={() => (stopAuto(), setLine({ ...line, at: i + 1 }))}
                >
                  {san}
                </button>
              </li>
            ))}
          </ol>
          {line.at > 0 && played.sans[line.at - 1] && (
            <p className="sk-dim">
              {played.sans[line.at - 1]}: {explainSan(played.sans[line.at - 1]!)}
            </p>
          )}
          <div className="sk-line-nav">
            <button className="btn" aria-label="Terug naar het begin" onClick={() => (stopAuto(), setLine({ ...line, at: 0 }))}>
              <NavIcon d="M7 5v14M18 6l-8 6 8 6V6Z" />
            </button>
            <button
              className="btn"
              aria-label="Zet terug"
              disabled={line.at === 0}
              onClick={() => (stopAuto(), setLine({ ...line, at: line.at - 1 }))}
            >
              <NavIcon d="M15 6l-7 6 7 6" />
            </button>
            <button
              className="btn"
              aria-label="Zet verder"
              disabled={line.at >= played.sans.length}
              onClick={() => (stopAuto(), setLine({ ...line, at: line.at + 1 }))}
            >
              <NavIcon d="M9 6l7 6-7 6" />
            </button>
          </div>
        </section>
      ) : (
        <section className="sk-review" aria-label="Wat er gebeurde">
          {scored && (
            <div className="sk-evals">
              <EvalBar cp={m.eval_before_cp ?? null} mate={m.eval_before_mate ?? null} label="Ervoor" />
              <EvalBar cp={m.eval_after_cp ?? null} mate={m.eval_after_mate ?? null} label={`Na ${m.played_san}`} />
              <p className="sk-dim sk-small">Stockfish, vanuit jou gezien: + is goed voor jou.</p>
            </div>
          )}
          <ul className="sk-moves">
            <li>
              <button
                className={`sk-move is-bad${focus === -1 ? ' is-focus' : ''}`}
                onClick={() => (refutation.length ? openRefutation() : setFocus(focus === -1 ? null : -1))}
              >
                <span className="sk-move-dot" aria-hidden>
                  ×
                </span>
                <span className="sk-move-san">{m.played_san}</span>
                <span className="sk-move-what">
                  jouw zet · {(MISTAKE_LABELS[m.kind]?.label ?? 'een fout').toLowerCase()}
                </span>
                {scored && (
                  <span className="sk-move-eval">
                    {formatEval({ cp: m.eval_after_cp ?? null, mate: m.eval_after_mate ?? null })}
                  </span>
                )}
              </button>
            </li>
            {better.map((b, i) => (
              <li key={b.move}>
                <button className={`sk-move is-good${focus === i ? ' is-focus' : ''}`} onClick={() => openBetter(i)}>
                  <span className="sk-move-dot" aria-hidden>
                    {i + 1}
                  </span>
                  <span className="sk-move-san">{b.san}</span>
                  <span className="sk-move-what">{explainSan(b.san)}</span>
                  {hasEval(b.cp, b.mate) && <span className="sk-move-eval">{formatEval(b)}</span>}
                </button>
              </li>
            ))}
          </ul>
          {refutation.length > 0 && (
            <button className="btn sk-why" onClick={openRefutation}>
              Waarom is {m.played_san} fout?
            </button>
          )}
          <p className="sk-dim sk-small">
            Tik een betere zet om te zien wat hij oplevert.
            {game && (
              <>
                {' '}
                <a href={gameLink(game, m.ply)} target="_blank" rel="noreferrer">
                  Bekijk de partij ↗
                </a>
              </>
            )}
          </p>

          <div className="sk-reasons" role="group" aria-label="Wat ging er mis in de partij?">
            <p className="sk-reasons-q">Wat ging er mis in de partij?</p>
            <div className="sk-chips">
              {MISTAKE_REASONS.map((r) => (
                <button key={r} className="sk-chip" aria-pressed={reason === r} onClick={() => onReason(r)}>
                  {REASON_INFO[r].label}
                </button>
              ))}
            </div>
            {reason && reason !== 'unsure' && <p className="sk-tip">{REASON_INFO[reason].advice}</p>}
          </div>
        </section>
      )}
    </>
  );
}
